import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import AsyncSelect from 'react-select/async';
import { toast } from 'react-toastify';
import { toastConfig } from '@/config';
import useRouteStore from '@/hooks/useRouteStore';
import { getContactList, addUsersToList, removeUsersFromList, getUsers } from '@/api';
import Table from '@/common/Table';

const roleBadge = {
  admin:   { label: 'Admin',   className: 'bg-red-100 text-red-700' },
  donor:   { label: 'Donador', className: 'bg-blue-100 text-blue-700' },
  user:    { label: 'Usuario', className: 'bg-green-100 text-green-700' },
  default: { label: 'Usuario', className: 'bg-gray-100 text-gray-600' },
};

const columns = (onRemove) => [
  { key: 'name',  title: 'Nombre', sortable: true },
  { key: 'email', title: 'Correo',  sortable: true },
  {
    key: 'role',
    title: 'Rol',
    sortable: true,
    width: '90px',
    mutator: (v) => {
      const { label, className } = roleBadge[v] ?? roleBadge.default;
      return (
        <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${className}`}>
          {label}
        </span>
      );
    },
  },
  {
    key: '_id',
    title: '',
    width: '80px',
    button: { callback: (id, row) => onRemove(row), text: 'Quitar' },
  },
];

const ContactList = () => {
  const { id } = useParams();
  const setRoute = useRouteStore((state) => state.setRoute);

  const [list, setList]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [adding, setAdding]     = useState(false);
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    fetchList();
  }, [id]);

  useEffect(() => {
    if (list) {
      setRoute([
        { name: 'Inicio',             url: '/administracion' },
        { name: 'Correos',            url: '/administracion/correos' },
        { name: 'Listas de contacto', url: '/administracion/correos/listas' },
        { name: list.name,            url: `/administracion/correos/listas/${id}` },
      ]);
    }
  }, [list]);

  const fetchList = () => {
    setLoading(true);
    getContactList(id).then((res) => {
      const data = res.data.result ?? res.data;
      setList(data);
    }).catch(() => {
      toast.error('Error al cargar la lista', toastConfig);
    }).finally(() => setLoading(false));
  };

  const loadUserOptions = async (inputValue) => {
    const res = await getUsers({ search: inputValue, page: 1 });
    const users = res.data.users ?? res.data ?? [];
    return users.map((u) => ({
      value: u._id,
      label: `${u.name} ${u.lastname} — ${u.email}`,
    }));
  };

  const handleAdd = async () => {
    if (!selected.length) return;
    setAdding(true);
    try {
      await addUsersToList(id, selected.map((u) => u.value));
      toast.success('Usuarios agregados', toastConfig);
      setSelected([]);
      fetchList();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error al agregar usuarios', toastConfig);
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (user) => {
    try {
      await removeUsersFromList(id, [user._id]);
      toast.success('Usuario eliminado de la lista', toastConfig);
      setList((prev) => ({ ...prev, users: prev.users.filter((u) => u._id !== user._id) }));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error al eliminar el usuario', toastConfig);
    }
  };

  if (loading) {
    return (
      <div className='font-montserrat w-full flex flex-col gap-2'>
        <div className='h-8 w-48 animate-pulse bg-gray-100 rounded mb-3' />
        {[...Array(5)].map((_, i) => (
          <div key={i} className='h-9 w-full animate-pulse bg-gray-100 rounded-lg' />
        ))}
      </div>
    );
  }

  const users = list?.users ?? [];

  return (
    <div className='font-montserrat w-full'>

      <div className='flex items-center justify-between pb-1 mb-5 border-b-2'>
        <div>
          <h1 className='font-semibold text-xl'>{list?.name}</h1>
          <p className='text-xs text-gray-400 mt-0.5'>{users.length} contacto{users.length !== 1 ? 's' : ''}</p>
        </div>
      </div>

      {/* Add users */}
      <div className='mb-5'>
        <p className='font-semibold text-red-1 mb-2'>Agregar usuarios</p>
        <div className='flex gap-2 items-center'>
          <div className='flex-1'>
            <AsyncSelect
              isMulti
              cacheOptions
              defaultOptions
              loadOptions={loadUserOptions}
              value={selected}
              onChange={setSelected}
              placeholder='Buscar usuarios...'
              noOptionsMessage={({ inputValue }) => inputValue ? 'Sin resultados' : 'Escribe para buscar...'}
              className='relative z-20'
            />
          </div>
          <button
            onClick={handleAdd}
            disabled={adding || !selected.length}
            className='flex items-center gap-1.5 bg-red-1 hover:bg-red-2 text-white text-sm font-semibold px-3 py-1.5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0'
          >
            {adding && (
              <svg className='animate-spin h-3.5 w-3.5' fill='none' viewBox='0 0 24 24'>
                <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
                <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 22 6.477 22 12h-4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z' />
              </svg>
            )}
            Agregar
          </button>
        </div>
      </div>

      {/* Users table */}
      <Table
        data={users}
        columns={columns(handleRemove)}
        elementsPerPage={20}
        tools={['search']}
      />

    </div>
  );
};

export default ContactList;
